/**
 * useAppAuth Hook
 * Composite hook that combines authentication, user, and permissions
 * Provides the most commonly needed auth-related functionality in one hook
 */

import { useAuth } from "../contexts/AuthContext";
import { useUser } from "../contexts/UserContext";
import { UserProfile, UserRole } from "../services/userService";

export interface UseAppAuthReturn {
  // Authentication
  isAuthenticated: boolean;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => void;
  getToken: () => string | null;

  // User & Roles
  user: UserProfile | null;
  selectedRole: UserRole | null;
  setSelectedRole: (role: UserRole) => Promise<void>;
  isUserConfigurationReady: boolean;
  showRoleModal: boolean;

  // Permissions
  hasPermission: (permission: string) => boolean;
  isPerfectServeUser: boolean;
  isPermissionsLoading: boolean;
  permissions: string[];
  refreshPermissions: () => Promise<void>;
  permissionsError: string | null;

  // Convenience computed properties
  isFullyLoaded: boolean;
  canAccessApp: boolean;
  needsRoleSelection: boolean;
}

export const useAppAuth = (): UseAppAuthReturn => {
  const auth = useAuth();
  const userContext = useUser();

  // Computed convenience properties
  const isFullyLoaded = !auth.loading && !userContext.isPermissionsLoading && auth.isAuthenticated;
  const canAccessApp =
    auth.isAuthenticated && !!userContext.user && !!userContext.selectedRole && userContext.isUserConfigurationReady;
  const needsRoleSelection =
    auth.isAuthenticated &&
    !!userContext.user &&
    userContext.isUserConfigurationReady &&
    !userContext.selectedRole &&
    userContext.showRoleModal;

  return {
    // Authentication
    ...auth,

    // User & Roles
    ...userContext,

    // Convenience computed properties
    isFullyLoaded,
    canAccessApp,
    needsRoleSelection,
  };
};
